import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://adextractor.algoridadigital.com/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        const refreshToken = localStorage.getItem('refreshToken');
        const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
          refresh_token: refreshToken
        });
        
        const { access_token, refresh_token } = response.data;
        localStorage.setItem('accessToken', access_token);
        localStorage.setItem('refreshToken', refresh_token);
        
        originalRequest.headers.Authorization = `Bearer ${access_token}`;
        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed, logout user
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }
    
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  logout: () => api.post('/auth/logout'),
  forgotPassword: (email) => api.post('/password/forgot', { email }),
  resetPassword: (token, password) => api.post('/password/reset', { token, password }),
  changePassword: (oldPassword, newPassword) => api.post('/password/change', {
    old_password: oldPassword,
    new_password: newPassword
  })
};

// License API
export const licenseAPI = {
  getAll: (params) => api.get('/license', { params }),
  validate: (data) => api.post('/license/validate', data),
  bindDevice: (data) => api.post('/license/bind-device', data),
  heartbeat: (data) => api.post('/license/heartbeat', data),
  logout: (data) => api.post('/license/logout', data),
  resetDevice: (data) => api.post('/license/reset-device', data)
};

// Admin API
export const adminAPI = {
  getDashboard: () => api.get('/admin/dashboard'),
  getUsers: (params) => api.get('/admin/users', { params }),
  createUser: (data) => api.post('/admin/users', data),
  updateUser: (id, data) => api.put(`/admin/users/${id}`, data),
  deleteUser: (id) => api.delete(`/admin/users/${id}`),
  getResellers: (params) => api.get('/admin/resellers', { params }),
  createReseller: (data) => api.post('/admin/resellers', data),
  getAdmins: (params) => api.get('/admin/admins', { params }),
  createAdmin: (data) => api.post('/admin/admins', data)
};

// Credit API
export const creditAPI = {
  getBalance: () => api.get('/credits/balance'),
  getTransactions: (params) => api.get('/credits/transactions', { params }),
  addCredits: (data) => api.post('/credits/add', data),
  removeCredits: (data) => api.post('/credits/remove', data),
  adjustCredits: (data) => api.post('/credits/adjust', data)
};

// Package API
export const packageAPI = {
  getAll: () => api.get('/admin/packages'),
  create: (data) => api.post('/admin/packages', data),
  update: (id, data) => api.put(`/admin/packages/${id}`, data),
  delete: (id) => api.delete(`/admin/packages/${id}`)
};

// SMTP API
export const smtpAPI = {
  getSettings: () => api.get('/admin/smtp/settings'),
  updateSettings: (data) => api.post('/admin/smtp/settings', data),
  testConnection: (data) => api.post('/admin/smtp/test', data)
};

// Audit Log API
export const auditAPI = {
  getLogs: (params) => api.get('/admin/audit-logs', { params })
};

export default api;
